import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FILE_UPLOAD_MESSAGE } from "constants/documents";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadFile from "static/images/UploadFile.svg";
import {
  invoiceApis,
  usePopulateBulkInvoiceMutation,
  useUploadBulkInvoiceMutation,
} from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import DownloadSimple from "static/images/DownloadSimple.svg";
import { openLink } from "utils/openLink";
import { FileIcon } from "components/design/FileIcon";
import { DeleteIcon } from "components/icons/delete";
import Loader from "components/DesignSystem/Loader/loader";
import { SolidCheck } from "components/icons/SolidCheck";
import { useDispatch } from "react-redux";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { useAnalytics } from "hooks/useAnalytics";
import {
  ADD_INVOICES_IN_BULK_BUTTON_CLICK,
  DOWNLOAD_BULK_UPLOAD_TEMPLATE,
  UPLOAD_BULK_UPLOAD_TEMPLATE,
} from "constants/analyticsEvents";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { Banner } from "components/DesignSystem/Banner/Banner";
import dayjs from "dayjs";
import { ConditionalLink } from "components/conditionalLink";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import { InvoiceInfoIcon } from "components/icons/InvoiceInfoIcon";

enum UploadContext {
  Filings = "filings",
  Invoices = "invoices",
  Reports = "reports",
}

type BulkInvoiceUploadProps = {
  isOpen: boolean;
  close: () => void;
  usePopulateMutation: typeof usePopulateBulkInvoiceMutation;
  useUploadMutation: typeof useUploadBulkInvoiceMutation;
  templateUrl: string;
  page?: "filings";
  onContactSupport?: () => void;
};

export const BulkInvoiceUpload = ({
  isOpen,
  close,
  usePopulateMutation,
  useUploadMutation,
  templateUrl,
  page,
  onContactSupport,
}: BulkInvoiceUploadProps) => {
  const { alertToast, successToast, info: infoToast } = useToast();
  const entityId = useCurrentEntityId();
  const [populateBulkInvoices, { isLoading: isPopulating }] =
    usePopulateMutation();
  const [
    uploadBulkInvoices,
    { data: bulkInvoiceData, isLoading: isUploading, reset },
  ] = useUploadMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { currentApp } = useCurrentAppContext();
  const { link } = useConstructInternalLink();
  const source =
    currentApp?.name === "Sales Tax"
      ? "sales_tax_nexus_tracker"
      : currentApp.name;

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    {
      skip: !entityId,
    }
  );

  const onDrop = async (files: File[]) => {
    trackEvent(UPLOAD_BULK_UPLOAD_TEMPLATE, {
      source,
    });

    try {
      await uploadBulkInvoices({
        file: files[0],
        entityId,
      }).unwrap();
      setSelectedFile(files[0]);
      successToast({ message: FILE_UPLOAD_MESSAGE });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const populateInvoices = async () => {
    trackEvent(ADD_INVOICES_IN_BULK_BUTTON_CLICK, {
      source,
    });
    try {
      if (bulkInvoiceData?.bulk_file_id) {
        await populateBulkInvoices({
          bulkFileId: bulkInvoiceData?.bulk_file_id,
          entityId,
        }).unwrap();

        setTimeout(() => {
          dispatch(invoiceApis.util.invalidateTags([{ type: "INVOICES" }]));
        }, 1000);
        infoToast({
          title: "Adding Invoices",
          message: "We will send a notification once it's complete.",
          duration: 5000,
        });
        close();
      }
    } catch (error) {
      alertToast({
        title: "Invoices could not be added",
        message:
          (error as BackendError).data?.error?.message ||
          "We found some error in the uploaded file. Please check and retry.",
      });
    }
  };

  const onDropRejected = () => {
    alertToast({ message: "Please upload a XLS or XLSX file exclusively." });
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      onDrop,
      maxSize: 25000000,
      accept: {
        "application/vnd.ms-excel": [".xls"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
      },
      onDropRejected,
      multiple: false,
    });

  const deleteFile = () => {
    setSelectedFile(null);
    reset();
  };

  const onDownloadSampleInvoice = () => {
    trackEvent(DOWNLOAD_BULK_UPLOAD_TEMPLATE, {
      source,
    });
    openLink(templateUrl);
  };
  const isSalesTax = currentApp?.name === "Sales Tax";

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>
            {isSalesTax
              ? "Add sales for any US state"
              : "Import Invoices from Excel"}
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body
          className={classNames({
            "t-pb-10": !bulkInvoiceData?.bulk_file_id,
          })}
        >
          <div className="t-text-body-sm t-text-text-100 t-mb-3">
            Download the pre-formatted template to ensure your data is properly
            structured. Add the invoice details to the template and upload it.
          </div>

          <div className="t-flex t-flex-col t-gap-5">
            <div>
              <Button size="small" onClick={onDownloadSampleInvoice}>
                <img
                  src={DownloadSimple}
                  alt="DownloadSimple"
                  className="t-mr-1"
                />
                <div>Download Excel template</div>
              </Button>
            </div>
            {isUploading && (
              <div className="t-h-40 t-flex t-justify-center t-mt-5 t-items-center">
                <Loader size="small" />
              </div>
            )}
            {!isUploading && (
              <>
                {selectedFile ? (
                  <div className="t-space-y-2">
                    <div className="t-text-body-sm t-text-text-30">
                      Uploaded file:
                    </div>
                    <div className="t-flex t-items-center t-justify-between t-p-3 t-rounded t-border t-border-solid t-border-neutral-20">
                      <div className="t-flex t-gap-3 t-items-center">
                        <FileIcon fileType={selectedFile.type} size="32" />
                        <div className="t-space-y-1">
                          <div className="t-text-subtitle-sm t-text-blue-100">
                            {selectedFile.name}
                          </div>
                          <div className="t-text-caption t-text-neutral-60">
                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                          </div>
                        </div>
                      </div>
                      <Button
                        size="small"
                        customType="ghost"
                        onClick={deleteFile}
                      >
                        <DeleteIcon color="currentColor" />
                      </Button>
                    </div>
                    <div className="t-flex t-gap-1 t-items-center">
                      <SolidCheck size="12px" />
                      <div className="t-text-text-30 t-text-body-sm">
                        Upload Completed
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={classNames(
                      "t-border-2 t-rounded-lg t-border-dashed t-border-neutral-20 t-px-6 t-py-8 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center t-flex-col t-gap-3 t-bg-surface-lighter-grey t-cursor-pointer",
                      {
                        "!t-border-green !t-text-green-70": isDragAccept,
                        "!t-border-red-50 !t-text-red-50": isDragReject,
                      }
                    )}
                    {...getRootProps()}
                    onClick={open}
                  >
                    <input {...getInputProps()} id="csv" />
                    <img src={UploadFile} alt="Upload File" />
                    Click or drag and drop to upload the filled template
                    <div className="t-text-body-sm t-text-text-30">
                      File Format: xls/xlsx
                    </div>
                  </div>
                )}
              </>
            )}

            {bookkeepingSettings?.accounting_lock_date && !isSalesTax && (
              <Banner
                type="neutral"
                title={
                  <span>
                    Your financials are locked until{" "}
                    {dayjs(bookkeepingSettings.accounting_lock_date).format(
                      "MMM DD, YYYY"
                    )}
                  </span>
                }
                description={
                  <span>
                    If any invoices are dated before this, the upload will fail.
                    You can either change the invoice dates manually, either
                    update the lock date in{" "}
                    <ConditionalLink
                      to={link("/books/books-settings")}
                      className="t-text-text-30 !t-underline"
                    >
                      settings,
                    </ConditionalLink>{" "}
                    or remove invoices from locked period and continue.
                  </span>
                }
              />
            )}
          </div>
          {page === UploadContext.Filings && (
            <div className="t-flex t-gap-2 t-items-center t-mt-2">
              <InvoiceInfoIcon />
              <p className="t-text-body-sm t-flex t-flex-row t-text-text-30 t-m-0 t-gap-1">
                Past period invoices will not be processed.{" "}
                <Button onClick={onContactSupport} customType="link">
                  Contact support
                </Button>
                to amend prior filings.
              </p>
            </div>
          )}
        </Modal.Body>
        {selectedFile && (
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button">Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={populateInvoices}
              isLoading={isPopulating}
              disabled={isPopulating}
            >
              Add Invoices
            </Button>
          </Modal.FooterButtonGroup>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
